import { searchOsmAddresses } from 'api/services/nominatimService'
import { splitCanadianUnitCivicAddress } from 'api/utils/canadianCivicAddress'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ParsedOsmAddress } from 'types/osmAddress'

interface OsmAddressSearchInputProps {
  id?: string
  label: string
  value: string
  placeholder?: string
  disabled?: boolean
  showLabel?: boolean
  labelClassName?: string
  inputClassName?: string
  inputTitle?: string
  maxLength?: number
  showError?: boolean
  errorMessage?: string
  onValueChange: (value: string) => void
  onBlur?: () => void
  onAddressSelect: (address: ParsedOsmAddress) => void
}

const OsmAddressSearchInput = ({
  id,
  label,
  value,
  placeholder,
  disabled = false,
  showLabel = true,
  labelClassName,
  inputClassName,
  inputTitle,
  maxLength,
  showError = false,
  errorMessage,
  onValueChange,
  onBlur,
  onAddressSelect,
}: OsmAddressSearchInputProps) => {
  const { t } = useTranslation()
  const [suggestions, setSuggestions] = useState<ParsedOsmAddress[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchRequestIdRef = useRef(0)
  /** Skip re-search when value was set programmatically after picking a suggestion. */
  const selectedValueRef = useRef<string | null>(null)

  const runSearch = useCallback(async (query: string, requestId: number) => {
    const trimmed = query.trim()
    if (trimmed.length < 3) {
      if (requestId === searchRequestIdRef.current) {
        setSuggestions([])
        setIsSearching(false)
      }
      return
    }

    setIsSearching(true)
    setSearchError(null)

    try {
      const results = await searchOsmAddresses(trimmed, {
        countryCodes: 'ca',
        limit: 6,
      })
      if (requestId !== searchRequestIdRef.current) return
      setSuggestions(results)
    } catch {
      if (requestId !== searchRequestIdRef.current) return
      setSuggestions([])
      setSearchError(t('createAd.flow.addressSearch.error'))
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsSearching(false)
      }
    }
  }, [t])

  useEffect(() => {
    if (selectedValueRef.current === value) {
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)

    const requestId = ++searchRequestIdRef.current
    debounceRef.current = setTimeout(() => {
      void runSearch(value, requestId)
    }, 450)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [value, runSearch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const closeSuggestions = useCallback(() => {
    searchRequestIdRef.current += 1
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSuggestions([])
    setIsOpen(false)
    setIsSearching(false)
    setSearchError(null)
  }, [])

  const handleSelect = (address: ParsedOsmAddress) => {
    // Keep Canada Post unit prefix typed by the user (e.g. "401- 2395 …" → unit 401).
    // Geocoders only return the civic building line, so unit must come from the query.
    const typed = splitCanadianUnitCivicAddress(value)
    const merged: ParsedOsmAddress = {
      ...address,
      unit: address.unit || typed.unit,
      street: (address.street || typed.streetLine || '').trim(),
    }
    const display = merged.street
    selectedValueRef.current = display
    closeSuggestions()
    // Parent sets street / App. / map — do not call onValueChange(formattedAddress):
    // that dropped the unit and could race-clear the map pin.
    onAddressSelect(merged)
    inputRef.current?.blur()
  }

  const showSuggestions = isOpen && suggestions.length > 0

  return (
    <div ref={containerRef} className="relative z-[20]">
      {showLabel ? (
        <label
          htmlFor={id}
          className={classNames(
            'mb-1.5 block text-sm font-semibold text-gray-800',
            labelClassName
          )}
        >
          {label}
        </label>
      ) : null}
      <input
        ref={inputRef}
        id={id}
        type="text"
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        title={inputTitle}
        maxLength={maxLength}
        value={value}
        onChange={e => {
          selectedValueRef.current = null
          onValueChange(e.target.value)
          setIsOpen(true)
        }}
        onBlur={onBlur}
        onFocus={() => {
          if (suggestions.length > 0 && selectedValueRef.current !== value) {
            setIsOpen(true)
          }
        }}
        aria-invalid={showError}
        className={
          inputClassName ??
          classNames(
            'w-full rounded-xl border py-3 px-4 focus:outline-none focus:ring-0',
            showError ? 'border-red-500' : 'border-gray-200'
          )
        }
      />

      {showError && errorMessage ? (
        <p className="mt-1.5 text-xs leading-snug text-red-600" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {isSearching && isOpen ? (
        <p className="absolute left-0 top-full z-[1100] mt-1 text-xs text-gray-500">
          {t('createAd.flow.addressSearch.searching')}
        </p>
      ) : null}

      {searchError && isOpen ? (
        <p className="absolute left-0 top-full z-[1100] mt-1 text-xs text-red-600" role="alert">
          {searchError}
        </p>
      ) : null}

      {showSuggestions ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-[1100] mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((item, index) => (
            <li key={`${item.formattedAddress}-${index}`}>
              <button
                type="button"
                role="option"
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(item)}
                className="block w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-imovPrimary/5"
              >
                <span className="block font-medium">{item.street || item.formattedAddress}</span>
                <span className="block text-xs text-gray-500">
                  {[item.neighborhood, item.city, item.postalCode]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default OsmAddressSearchInput
